import { createContext, useContext, useCallback, useEffect, useState, useRef, type ReactNode } from "react";
import { useSearch } from "wouter";
import {
  CMSState,
  CMSIncomingMessage,
  CMSUILanguage,
  CMS_ORIGINS,
  languageDtoToCode,
} from "@/lib/cms-types";

interface CMSContextValue extends CMSState {
  pageId: string | null;
  initialContent: string | null;
  setUILanguage: (language: CMSUILanguage) => void;
  setDirty: (dirty: boolean) => void;
  clearDirty: () => void;
  getHtml: () => string;
  setHtmlGetter: (getter: () => string) => void;
}

const CMSContext = createContext<CMSContextValue | null>(null);

export function useCMS() {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error("useCMS must be used within a CMSProvider");
  }
  return context;
}

export function useCMSSafe() {
  return useContext(CMSContext);
}

interface CMSProviderProps {
  children: ReactNode;
}

export function CMSProvider({ children }: CMSProviderProps) {
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  
  const urlLanguage = (searchParams.get("language") || "en") as CMSUILanguage;
  const urlContestId = searchParams.get("contestId");
  const urlEditionId = searchParams.get("editionId");
  const urlTargetLanguage = searchParams.get("targetLanguage");
  const urlPageId = searchParams.get("pageId");
  
  const isEmbedded = typeof window !== "undefined" && window.parent !== window;
  
  const [pageId] = useState<string | null>(urlPageId);
  const [uiLanguage, setUILanguage] = useState<CMSUILanguage>(
    ["en", "es", "pl"].includes(urlLanguage) ? urlLanguage : "en"
  );
  const [targetLanguage, setTargetLanguage] = useState<string>(
    urlTargetLanguage ? languageDtoToCode(urlTargetLanguage) : "en"
  );
  const [contestId] = useState<string | null>(urlContestId);
  const [editionId] = useState<string | null>(urlEditionId);
  const [isDirty, setIsDirty] = useState(false);
  const [initialContent, setInitialContent] = useState<string | null>(null);
  
  const htmlGetterRef = useRef<(() => string) | null>(null);
  const lastOriginRef = useRef<string | null>(null);
  const initialContentReceivedRef = useRef(false);
  const readySentRef = useRef(false);

  const getHtml = useCallback(() => {
    if (htmlGetterRef.current) {
      return htmlGetterRef.current();
    }
    return "";
  }, []);

  const setHtmlGetter = useCallback((getter: () => string) => {
    htmlGetterRef.current = getter;
  }, []);

  const setDirty = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
    if (isEmbedded && lastOriginRef.current) {
      console.log(`[CMS] Sending set-dirty: ${dirty}`);
      window.parent.postMessage(
        { action: "set-dirty", data: { dirty } },
        lastOriginRef.current
      );
    }
  }, [isEmbedded]);
  
  const clearDirty = useCallback(() => {
    setIsDirty(false);
    if (isEmbedded && lastOriginRef.current) {
      window.parent.postMessage(
        { action: "set-dirty", data: { dirty: false } },
        lastOriginRef.current
      );
    }
  }, [isEmbedded]);

  const sendHtmlResponse = useCallback((requestId: string, origin: string) => {
    const html = getHtml();
    console.log(`[CMS] Responding to get-generated-html (requestId: ${requestId}, origin: ${origin}, htmlLength: ${html.length}, hasContent: ${html.length > 0})`);
    window.parent.postMessage(
      { action: "generated-html", data: { requestId, html } },
      origin
    );
    setIsDirty(false);
    window.parent.postMessage(
      { action: "set-dirty", data: { dirty: false } },
      origin
    );
  }, [getHtml]);

  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      if (!CMS_ORIGINS.has(event.origin)) {
        console.warn("Ignoring message from unknown origin:", event.origin);
        return;
      }

      lastOriginRef.current = event.origin;
      const message = event.data as CMSIncomingMessage;

      if (!message || typeof message.action !== "string") {
        return;
      }

      console.log(`[CMS] Received postMessage action: "${message.action}" from origin: ${event.origin}`);

      switch (message.action) {
        case "change-language":
          if (message.data?.language && ["en", "es", "pl"].includes(message.data.language)) {
            console.log(`[CMS] Language changed to: ${message.data.language}`);
            setUILanguage(message.data.language);
          }
          break;

        case "get-generated-html":
          if (message.data?.requestId) {
            console.log(`[CMS] get-generated-html requested (requestId: ${message.data.requestId})`);
            sendHtmlResponse(message.data.requestId, event.origin);
          }
          break;

        case "set-initial-content":
          console.log(`[CMS] set-initial-content received (alreadyReceived: ${initialContentReceivedRef.current}, htmlLength: ${typeof message.data?.html === "string" ? message.data.html.length : "N/A"})`);
          if (!initialContentReceivedRef.current && message.data && typeof message.data.html === "string") {
            initialContentReceivedRef.current = true;
            setInitialContent(message.data.html);
          }
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    if (!readySentRef.current) {
      readySentRef.current = true;
      console.log("[CMS] Sending 'ready' to parent");
      window.parent.postMessage({ action: "ready" }, "*");
    }

    return () => window.removeEventListener("message", handleMessage);
  }, [isEmbedded, sendHtmlResponse]);

  const value: CMSContextValue = {
    pageId,
    initialContent,
    uiLanguage,
    targetLanguage,
    contestId,
    editionId,
    isEmbedded,
    isDirty,
    setUILanguage,
    setDirty,
    clearDirty,
    getHtml,
    setHtmlGetter,
  };

  return <CMSContext.Provider value={value}>{children}</CMSContext.Provider>;
}
